<template>
  <div class="recommend-card">
    <!-- 公司名称 -->
    <div class="company-name">{{ card.name }}</div>

    <!-- 记录集 -->
    <div class="record-set">
      <!-- 记录 -->
      <template v-for="(item, index) in dataList" :key="index">
        <div class="record">
          <!-- 步骤条 -->
          <div class="step-bar">
            <div
              v-if="index === 0 || item.lastConnectTime !== dataList[index - 1].lastConnectTime"
              class="step-icon"
            >
              <i class="icon-edit-ai-line t-iconfont"></i>
            </div>
            <div class="step-line"></div>
          </div>

          <!-- 内容 -->
          <div class="content">
            <!-- 标题 -->
            <div
              v-if="index === 0 || item.lastConnectTime !== dataList[index - 1].lastConnectTime"
              class="title"
            >
              <div class="follow-info">
                <span class="follow-time">{{ showTime(item.lastConnectTime) }} 跟进提炼</span>
              </div>
              <div class="action" @click="handleOpenDetail(item)">
                <span class="action-text">跟进详情</span>
                <i class="icon-arrow-right-s-line t-iconfont"></i>
              </div>
            </div>

            <template v-for="field in item.list" :key="field.detailId">
              <!-- 字段 -->
              <div class="field">
                <!-- 客户信息 -->
                <div class="customer-info">
                  <h4 class="info-title">{{ field.taskContent }}</h4>
                  <div class="info-detail">
                    <div class="detail-label">
                      <i class="icon-calendar2-ai-line t-iconfont"></i>
                      <span class="label-text">推荐拜访时间：</span>
                    </div>
                    <span class="detail-value">{{ showTime(field.recommendTime) }}</span>
                  </div>
                  <div class="suggestion-references">
                    <ReferenceCard
                      v-for="(evidenceItem, evidenceIndex) in field.evidence || []"
                      :key="`${evidenceItem.fileName}-${evidenceIndex}`"
                      :file-name="evidenceItem.fileName"
                    />
                  </div>
                </div>

                <!-- 选择器 -->
                <div class="selector">
                  <p class="selector-text">{{ field.recommendReason }}</p>
                </div>

                <!-- 操作组 -->
                <div class="action-group">
                  <button class="btn ignore-btn" @click="handleIgnore(field)">
                    <i class="btn-icon icon-eye-off-line t-iconfont"></i>
                    <span class="btn-text">忽略</span>
                  </button>
                  <button class="add-plan-btn btn" @click="handleAddPlan(field)">
                    <i class="btn-icon icon-briefcase-ai-line t-iconfont"></i>
                    <span class="btn-text">加入访客计划</span>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
      <div v-if="page < pageTotal" class="load-more-btn" @click="handleLoadMore">点击加载更多</div>
    </div>
    <AiDetailDrawer v-model="detailVisible" :detail-query="detailQuery"></AiDetailDrawer>

    <AiUseXbbForm
      v-if="xbbUrl && showXbb"
      v-model:loading="iframeLoading"
      v-model:show-xbb="showXbb"
      :xbb-url="xbbUrl"
      @handleXbbSuccess="handleXbbSuccess"
    />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { getCustomRecordInfo, blindAddPlan } from '@/api'
import { getRecommendedTaskList, ignorePlan } from '@/api/app-home'
import xbb from '@xbb/xbb-utils'
import { isMobile } from '@/utils'
import AiDetailDrawer from '@/components/ai-detail-drawer/index.vue'
import AppMessage from '@/components/message/index.js'
import AiUseXbbForm from '@/components/ai-use-xbbform/index.vue'
import ReferenceCard from '@/components/reference-card/index.vue'

defineOptions({ name: 'RecommendCard' })

const emits = defineEmits(['pcAddPlan', 'openPcDetail'])

const props = defineProps({
  card: {
    type: Object,
    required: true
  }
})

const page = ref(1)
const pageSize = ref(10)
const pageTotal = ref(0)

const dataList = ref([])
const detailVisible = ref(false)
const detailQuery = ref({})

const iframeLoading = ref(false)
const showXbb = ref(false)
const xbbUrl = ref('')
const detailId = ref('')

const showTime = (time) => {
  return xbb.formatTime(time, 'yyyy-MM-dd HH:mm')
}

const getParams = async (type) => {
  const res = await getCustomRecordInfo({ businessType: type })
  const { appId, menuId, formId, businessType, subBusinessType, frontUrl } = res.result
  return { appId, menuId, formId, businessType, subBusinessType, frontUrl }
}

const handleAddPlan = async (field) => {
  detailId.value = field.detailId
  if (isMobile()) {
    iframeLoading.value = true
    const path = 'application/newForm'
    const res = await getParams(601)
    const { appId, menuId, formId, businessType, subBusinessType, frontUrl } = res
    const targetUrl = frontUrl
    // const targetUrl = xbb.isDevEnv() ? 'http://localhost:8080/' : frontUrl
    xbbUrl.value = `${targetUrl}?isAi=1&detailId=${
      detailId.value
    }#/${path}?appId=${appId}&menuId=${menuId}&formId=${formId}&saasMark=1&distributorMark=0&businessType=${businessType}&subBusinessType=${subBusinessType}&timestamp=${new Date().getTime()}&isAiRoot=1`
    showXbb.value = true
  } else {
    emits('pcAddPlan', detailId.value)
  }
}

const handleXbbSuccess = (e) => {
  showXbb.value = false
  AppMessage.success('新建成功')
  blindAddPlan({
    detailId: detailId.value,
    communicatePlanId: e.data.dataId
  }).then(() => {
    page.value = 1
    getInsideList()
  })
}

const handleIgnore = (field) => {
  ignorePlan({
    detailId: field.detailId
  }).then(() => {
    AppMessage.success('操作成功')
    page.value = 1
    getInsideList()
  })
}

const handleLoadMore = () => {
  page.value++
  getInsideList()
}

const getInsideList = async () => {
  const res = await getRecommendedTaskList({
    page: page.value,
    pageSize: pageSize.value,
    businessType: props.card.businessType,
    dataId: props.card.dataId
  })
  pageTotal.value = res.result.pageHelper.pageTotal
  if (page.value === 1) {
    dataList.value = res.result.items
  } else {
    dataList.value = [...dataList.value, ...res.result.items]
  }
  console.log('推荐任务列表：', res)
}

const handleOpenDetail = (item) => {
  console.log('打开跟进详情，item：', item)

  if (isMobile()) {
    detailQuery.value = {
      id: item.communicateBehavingId,
      customer: props.card.dataId,
      name: props.card.name,
      businessType: props.card.businessType
    }
    detailVisible.value = true
  } else {
    emits('openPcDetail', {
      appId: props.card.appId,
      behavingId: item.communicateBehavingId,
      id: item.communicateBehavingId,
      dataId: props.card.dataId,
      businessType: props.card.businessType
    })
  }
}

onMounted(() => {
  getInsideList()
})
</script>

<style lang="scss" scoped>
.recommend-card {
  margin: 12px 12px 0 12px;
  background-color: #ffffff;
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.05);
  border-radius: 16px;

  .company-name {
    font-size: 16px;
    font-weight: 600;
    color: #323233;
    margin: 0;
    padding: 12px 0;
    margin: 0 12px;
    border-bottom: 1px dashed #f2f3f5;
  }

  .record-set {
    padding: 12px;

    .record {
      display: flex;

      .step-bar {
        width: 14px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: 8px;

        .step-icon {
          width: 14px;
          height: 14px;
          color: #969799;
          margin-top: 8px;
          margin-bottom: 8px;

          .icon {
            width: 10.5px;
            height: 11.65px;
          }
        }

        .step-line {
          flex: 1;
          width: 1px;
          background-color: #f2f3f5;
        }
      }

      .content {
        flex: 1;

        .title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 8px;
          margin-bottom: 8px;

          .follow-info {
            .follow-time {
              line-height: 16px;
              font-size: 12px;
              color: #323233;
            }
          }

          .action {
            display: flex;
            align-items: center;

            .action-text {
              line-height: 16px;
              font-size: 12px;
              color: #969799;
              margin-right: 4px;
              cursor: pointer;
            }

            i {
              font-size: 14px;
              color: #969799;
            }
          }
        }

        .field {
          background-color: #f7f8fa;
          border: 1px solid #f2f3f5;
          border-radius: 12px;
          padding: 12px;
          margin-bottom: 8px;

          .customer-info {
            margin-bottom: 12px;

            .info-title {
              font-size: 14px;
              font-weight: 500;
              color: #323233;
              margin: 0 0 4px 0;
            }

            .info-detail {
              display: flex;
              align-items: center;

              .detail-label {
                display: flex;
                align-items: center;
                i {
                  font-size: 12px;
                  color: #969799;
                  margin-right: 4px;
                }

                .label-text {
                  font-size: 12px;
                  color: #969799;
                  line-height: 16px;
                }
              }

              .detail-value {
                font-size: 12px;
                color: #323233;
              }
            }

            .suggestion-references {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-top: 8px;
            }
          }

          .selector {
            background: linear-gradient(289deg, #eef2ff 1%, #f5f3ff 100%);
            border: 1px solid #f5e8ff;
            border-radius: 12px;
            padding: 12px;
            margin-bottom: 12px;

            .selector-text {
              font-size: 13px;
              color: #323233;
              margin: 0;
              line-height: 1.4;
            }
          }

          .action-group {
            display: flex;

            .btn {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid;
              border-radius: 64px;
              padding: 8px 12px;
              background: transparent;
              cursor: pointer;

              &:first-child {
                margin-right: 12px;
              }

              .btn-icon {
                width: 14px;
                height: 14px;
                margin-right: 8px;
              }

              .btn-text {
                font-size: 13px;
              }
            }

            .ignore-btn {
              border-color: #e5e6eb;

              .btn-icon,
              .btn-text {
                color: #646566;
              }
            }

            .add-plan-btn {
              border-color: #7a52f4;

              .btn-icon,
              .btn-text {
                color: #7a52f4;
              }
            }
          }
        }
      }
    }
    .load-more-btn {
      text-align: center;
      margin-top: 12px;
      font-size: 13px;
      color: #969799;
      cursor: pointer;
    }
  }
}
</style>
